import React from 'react';
import {
  ActionIcon,
  Badge,
  DetailStack,
  FontWeight, HStack, ICON_EYE_OUTLINE, Text, UserPreview,
  SimpleTable,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { SerpInvoice } from '../../../providers/api/dtos';
import ViewContactPopoverPreview from '../../../contact/containers/view-contact-popover-preview/ViewContactPopoverPreview';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import ViewCompanyPopoverPreview from '../../../company/containers/view-company-popover-preview/ViewCompanyPopoverPreview';
import CompanyAvatar from '../../../company/components/company-avatar/CompanyAvatar';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import EntityReferenceBadge from '../../../components/entity-reference-badge/EntityReferenceBadge';
import { InvoiceStatus, ReferenceType } from '../../../domain/types';
import { invoiceTypeLabels } from '../../../labels';
import UpdateInvoiceStatus, { UpdateInvoiceStatusProps } from '../update-invoice-status/UpdateInvoiceStatus';
import { ViewAgentSmallPreview } from '../../../agent/components';
import ViewAgentPopoverPreview from '../../../agent/containers/view-agent-popover-preview/ViewAgentPopoverPreview';
import { toAgentPreviewProps } from '../../../agent/mappers/toAgentPreviewProps';
import InvoiceValuePopover from '../../components/invoice-value-popover/InvoiceValuePopover';
import { goToDetailPage } from '../../../utils/navigate/utils';
import DateViewer from '../../../components/date-viewer/DateViewer';
import { getCriticalIfInThePast, getPaymentDateLabel } from '../utils';
import { computeGrossValue } from '../../utils';
import * as styles from './ViewSerpInvoiceTableRow.style';
import TextPreview from '../../../components/text-preview/TextPreview';

export interface ViewSerpInvoiceTableRowProps {
  serpInvoice: SerpInvoice,
  onInvoiceUpdated?: UpdateInvoiceStatusProps['onSuccess'],
  canEdit?: boolean,
}

const ViewSerpInvoiceTableRow: React.FC<ViewSerpInvoiceTableRowProps> = ({
  serpInvoice,
  canEdit = false,
  onInvoiceUpdated,
}) => {
  const {
    invoice, property, contact, company, agent,
  } = serpInvoice;

  const paymentDateLabel = React.useMemo(() => getPaymentDateLabel(invoice.status), [invoice.status]);
  const grossValue = React.useMemo(() => computeGrossValue(invoice.value, invoice.vat), [invoice.value, invoice.vat]);
  const amountLabel = grossValue !== undefined ? 'Importo' : 'Importo (netto)';
  const displayAmount = grossValue ?? invoice.value;

  const noteToDisplay = React.useMemo(
    () => invoice.notes?.[0],
    [invoice.notes],
  );

  const goToInvoiceDetailPage = React.useCallback(() => {
    goToDetailPage(invoice.id!, ReferenceType.INVOICE);
  }, [invoice.id]);

  return (
    <SimpleTable.Row>
      <SimpleTable.Cell aria-label="Informazioni sull'entità" width="12rem">
        {contact && (
          <ViewContactPopoverPreview
            contact={contact}
            trigger={(
              // eslint-disable-next-line react/no-unknown-property
              <div css={styles.userWrapper}>
                <UserPreview
                  name={contact.name || NO_VALUE_SYMBOL}
                  size="S"
                >
                  {contact.phoneNumber || NO_VALUE_SYMBOL}
                </UserPreview>
              </div>
            )}
          />
        )}

        {company && (
          <ViewCompanyPopoverPreview
            company={company}
            trigger={(
              <HStack gap={100} wrap="nowrap">
                <CompanyAvatar size="S" />
                <Text.BodySmall
                  fontWeight={FontWeight.MEDIUM}
                  css={setVerticalEllipsis(2)}
                >
                  {company.name}
                </Text.BodySmall>
              </HStack>
            )}
          />
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Informazioni sull'immobile">
        <EntityReferenceBadge
          id={property.id!}
          entity={ReferenceType.PROPERTY}
          label={property.referenceId!}
          isNewTab
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Tipo del pagamento">
        <Badge
          label={invoiceTypeLabels[invoice.type]}
          color="neutral"
          size="XS"
          emphasis="low"
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Stato del pagamento">
        <UpdateInvoiceStatus
          invoice={invoice}
          canEditType={canEdit}
          emphasis="low"
          size="XS"
          onSuccess={onInvoiceUpdated}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Agente"
        width="2rem"
      >
        <ViewAgentPopoverPreview
          agent={agent}
          trigger={(
            <ViewAgentSmallPreview
              {...toAgentPreviewProps(agent, { omit: { name: true } })}
              size="S"
              expanded
            />
          )}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Importo del pagamento">
        <DetailStack label={amountLabel}>
          <InvoiceValuePopover
            invoice={invoice}
            trigger={(
              <Text.Caption
                fontWeight={FontWeight.REGULAR}
                whitespace="nowrap"
                lineHeight="1"
              >
                {formatEuro(displayAmount)}
              </Text.Caption>
            )}
          />
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Data del pagamento"
        width="5rem"
      >
        <DetailStack label={paymentDateLabel}>
          <DateViewer
            stringDate={invoice.paymentDate}
            highlightWhen={invoice.status !== InvoiceStatus.COLLECTED ? getCriticalIfInThePast : undefined}
          />
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell
        withDivider={invoice.status !== InvoiceStatus.DRAFT}
      >
        <HStack gap={200} wrap="nowrap">
          {[InvoiceStatus.PAID, InvoiceStatus.COLLECTED].includes(invoice.status) && (
            <DetailStack
              label="Rif. Fattura"
              aria-label="Identificato riferimento fattura"
            >
              {invoice.invoiceId}
            </DetailStack>
          )}
          {[InvoiceStatus.PROFORMA, InvoiceStatus.NOT_PAID].includes(invoice.status) && (
            <DetailStack
              label="Rif. Proforma"
              aria-label="Identificato riferimento proforma"
            >
              {invoice.proformaInvoiceId}
            </DetailStack>
          )}
        </HStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell>
        {noteToDisplay && (
          <TextPreview
            aria-label="Note sul pagamento"
            text={noteToDisplay.text}
            author={noteToDisplay.author}
            date={noteToDisplay.date}
          />
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell align="right">
        <ActionIcon
          label="Visualizza"
          aria-label="Visualizza dettagli pagamento"
          icon={{ path: ICON_EYE_OUTLINE }}
          size="S"
          onClick={goToInvoiceDetailPage}
        />
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewSerpInvoiceTableRow;
